import React from 'react';
import PropTypes from 'prop-types';
import GoogleMap from '../google-map/GoogleMap';
import SearchFieldPlaces from './SearchFieldPlaces';
import MapContactCard from '../map-contact-card/MapContactCard';
import AreaFilter from './AreaFilter';
import { installersToContactInfoProps } from './utils/parsers';
import { getAreas, filterInstallersByAreas, getAreasFromUrl, setAreasInUrl } from './utils/areaFilter';
import { setLocationInUrl } from './utils/location';

const MapView = (props) => {
    const [selectedLocationCords, setSelectedLocationCords] = React.useState(props.selectedLocationCords);
    const [places, setPlaces] = React.useState();
    const [visiblePlaces, setVisiblePlaces] = React.useState([]);
    const [selectedPlace, setSelectedPlace] = React.useState('');
    const [selectedAreas, setSelectedAreas] = React.useState(getAreasFromUrl);
    const [isFilterOpen, setIsFilterOpen] = React.useState(selectedAreas.length > 0);
    const mapRef = React.useRef();

    const areas = React.useMemo(() => (places ? getAreas(places) : []), [places]);
    const filteredPlaces = React.useMemo(() => (
        places ? filterInstallersByAreas(places, selectedAreas) : places
    ), [places, selectedAreas]);

    const onAreasChange = (newAreas) => {
        setSelectedAreas(newAreas);
        setAreasInUrl(newAreas);
    };

    const onLocationChange = (cords, query) => {
        setSelectedLocationCords(cords);
        setLocationInUrl(cords, query);
    };

    const openInfoWindow = (id) => {
        mapRef.current.openInfoWindow(id);
    };

    React.useEffect(() => {
        if (mapRef.current) {
            openInfoWindow(selectedPlace);
        }
    }, [selectedPlace, mapRef]);

    React.useEffect(() => {
        fetch(props.placesEndpoint)
            .then((res) => res.json())
            .then((data) => setPlaces(data.Result));
    }, []);

    return (
        <div className="find-installer-map">
            <aside className="find-installer-map__result">
                <div className="row">
                    <div className="col col--span-12">
                        <div className="find-installer-map__search">
                            <SearchFieldPlaces
                                onSelect={onLocationChange}
                                inputValue={props.selectedLocationQuery}
                                label={props.lang.searchLabel}
                                geolocationLabel={props.lang.geolocation}
                            >
                                {areas.length > 0 &&
                                    <button
                                        type="button"
                                        className={`button button--ghost search-field__icon-button ${isFilterOpen ? 'search-field__icon-button--active' : ''}`}
                                        aria-label={props.lang.filterToggle}
                                        aria-expanded={isFilterOpen}
                                        aria-controls="find-installer-filter"
                                        onClick={() => setIsFilterOpen((open) => !open)}
                                    >
                                        <span className="search-field__icon search-field__icon--filter" aria-hidden="true" />
                                    </button>
                                }
                            </SearchFieldPlaces>
                        </div>
                        {areas.length > 0 && isFilterOpen &&
                            <AreaFilter
                                id="find-installer-filter"
                                title={props.lang.filterTitle}
                                placeholder={props.lang.filterPlaceholder}
                                removeLabel={props.lang.filterRemove}
                                areas={areas}
                                selectedAreas={selectedAreas}
                                onChange={onAreasChange}
                            />
                        }
                    </div>
                </div>
                <div className="find-installer-map__cards">
                    <div className="row">
                        {filteredPlaces
                            ? installersToContactInfoProps(filteredPlaces, selectedLocationCords).map(({ contactInfo, key, title }) => (
                                visiblePlaces.includes(key) &&
                                <div className="col col--span-12 col--span-m-6 col--span-xl-12" key={key}>
                                    <MapContactCard
                                        key={key}
                                        id={String(key)}
                                        title={title}
                                        contactInfo={contactInfo}
                                        active={selectedPlace === key}
                                        onClick={() => setSelectedPlace(key)}
                                    />
                                </div>
                            ))
                            // ToDo: Add loading state
                            : null}
                    </div>
                </div>
            </aside>
            <div className="find-installer-map__map">
                {filteredPlaces
                    ? <GoogleMap
                        ref={mapRef}
                        places={filteredPlaces}
                        targetPlaceCords={selectedLocationCords}
                        onVisibleMarkersUpdate={setVisiblePlaces}
                        onInfoWindowClose={() => setSelectedPlace(() => '')}
                        onMarkerClick={(marker) => setSelectedPlace(() => marker.Id)}
                    />
                    // ToDo: Add loading state
                    : null}
            </div>
        </div>
    );
};

MapView.propTypes = {
    selectedLocationCords: PropTypes.object.isRequired,
    selectedLocationQuery: PropTypes.string,
    setSearchLatLon: PropTypes.shape({
        lat: PropTypes.number,
        lon: PropTypes.number
    }),
    placesEndpoint: PropTypes.string.isRequired,
    lang: PropTypes.shape({
        searchLabel: PropTypes.string,
        geolocation: PropTypes.string,
        filterToggle: PropTypes.string,
        filterTitle: PropTypes.string,
        filterPlaceholder: PropTypes.string,
        filterRemove: PropTypes.string
    })
};

MapView.defaultProps = {
    lang: {}
};

export default MapView;
